import { useEffect, useState } from 'react'
import { API_BASE_URL, callsAPI, developerAPI } from '../../services/api'
import type { TradingViewSetup } from '../../types'

/**
 * Lock calls from TradingView alerts: the webhook URL, a key with the
 * calls:lock scope (shown once), and the alert message to paste. The server
 * still decides the entry: the live Kraken price when the alert arrives.
 */
const TradingViewAlerts = () => {
  const [setup, setSetup] = useState<TradingViewSetup | null>(null)
  const [rawKey, setRawKey] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    callsAPI
      .tradingviewSetup()
      .then((s) => live && setSetup(s))
      .catch(() => live && setSetup(null))
    return () => {
      live = false
    }
  }, [])

  if (!setup) return null

  const url = `${API_BASE_URL.replace(/\/$/, '')}${setup.webhook_path}`
  const message = JSON.stringify({ ...setup.template, key: rawKey ?? setup.template.key }, null, 2)

  const createKey = async () => {
    setCreating(true)
    setError(null)
    try {
      const key = await developerAPI.createKey({
        name: 'TradingView alerts',
        expires_in_days: null,
        scopes: setup.scope,
      })
      setRawKey(key.raw_key)
    } catch {
      setError('The key could not be created. Try again, or revoke an old key in Settings → Developer.')
    } finally {
      setCreating(false)
    }
  }

  return (
    <section
      className="rounded-lg border border-terminal-border bg-terminal-panel p-4"
      aria-labelledby="tv-alerts-title"
      data-testid="tradingview-alerts"
    >
      <h2 id="tv-alerts-title" className="text-lg font-semibold text-gray-900 dark:text-gray-50">
        Lock calls from TradingView alerts
      </h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        An alert that fires locks a call exactly as the form does: at the live Kraken price when the alert
        arrives (not your chart's price), within the same limits, public and permanent. Stop and target are
        prices.
      </p>

      <ol className="mt-3 list-decimal space-y-3 pl-5 text-sm text-gray-700 dark:text-gray-300">
        <li>
          In the alert's Notifications, turn on Webhook URL and paste:
          <code className="mt-1 block break-all rounded bg-black/30 px-2 py-1 font-mono text-xs" data-testid="tv-url">
            {url}
          </code>
        </li>
        <li>
          {rawKey ? (
            <span data-testid="tv-key-created">
              Your key is in the message below. It is shown only now: anyone with it can lock calls as you, so
              keep it in the alert and nowhere else. Revoke it in Settings → Developer.
            </span>
          ) : (
            <>
              Create a key that can lock calls (and nothing else):{' '}
              <button
                type="button"
                onClick={createKey}
                disabled={creating}
                className="ml-1 rounded-md bg-accent px-3 py-1 text-xs font-semibold text-terminal-bg hover:bg-accent-hover disabled:opacity-60"
                data-testid="tv-create-key"
              >
                {creating ? 'Creating…' : 'Create key'}
              </button>
            </>
          )}
          {error && (
            <p className="mt-1 text-red-300" role="alert">
              {error}
            </p>
          )}
        </li>
        <li>
          Set the alert's Message to this, with your direction, stop and target ({setup.expiries.join(', ')}{' '}
          for expiry):
          <pre className="mt-1 overflow-x-auto rounded bg-black/30 px-2 py-1 font-mono text-xs" data-testid="tv-message">
            {message}
          </pre>
        </li>
      </ol>
    </section>
  )
}

export default TradingViewAlerts
